import useSimpleMuiTableBuilder from 'hooks/useSimpleMuiTableBuilder';
import { TableBuilderCellProps } from 'types';
import { falsyCheckDate } from 'utils';
import { StyledAlert } from 'shared/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';
import styled from 'styled-components';
import useCsvImportStatus, { FormattedCsvImportStatus } from 'hooks/useCsvImportStatus';
import { MappingStatusDataCell } from './DataCell/MappingStatusDataCell';

const RefreshButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 2rem;
`;

const csvImportStatusTableHeadCellIds = {
  CREATED_AT: 'createdAt',
  DATA_TYPE: 'dataType',
  SESSION_ID: 'sessionId',
  MAPPING_STATUS: 'mappingStatus',
  PENDING_WITHOUT_ERROR_IS: 'pendingWithoutErrorCountInIS',
  TOTAL_ENTRIES: 'totalEntries',
  IS_AGENT_FETCH_FINISHED: 'isAgentFetchFinish', // red or green
  STATUS: 'status', // the sync status on IS
  ERROR_ON_IS: 'errorCountInIS',
  DELETED_AT: 'deletedAt',
  DELETED_BY: 'deleteAccountId',
};

type TableProps = ReturnType<typeof useCsvImportStatus> & {
  refetchCsvImportStatus: () => void;
};

const csvImportStatusTableCellProps: TableBuilderCellProps = {
  headCells: [
    {
      id: csvImportStatusTableHeadCellIds.CREATED_AT,
      label: 'Date initiated',
    },
    {
      id: csvImportStatusTableHeadCellIds.DATA_TYPE,
      label: 'Data Type',
    },
    {
      id: csvImportStatusTableHeadCellIds.SESSION_ID,
      label: 'Session ID',
    },
    {
      id: csvImportStatusTableHeadCellIds.TOTAL_ENTRIES,
      label: 'Total Entries',
      align: 'center',
      tooltip:
        'Total number of entries in the import file. Any CSV import before 2022-02-11 will not have this count',
      tooltipPlacement: 'top',
    },
    {
      id: csvImportStatusTableHeadCellIds.PENDING_WITHOUT_ERROR_IS,
      label: 'Pending without error on Integration Server',
    },
    {
      id: csvImportStatusTableHeadCellIds.ERROR_ON_IS,
      label: 'Error on Integration Server',
    },
    {
      id: csvImportStatusTableHeadCellIds.MAPPING_STATUS,
      label: 'Mapping Status',
      tooltip: 'Number of entries on Mosaic and their status',
      tooltipPlacement: 'top',
    },
    {
      id: csvImportStatusTableHeadCellIds.IS_AGENT_FETCH_FINISHED,
      label: 'Is Finished',
      tooltip: 'Complete the import to the backend',
      tooltipPlacement: 'top',
    },
    {
      id: csvImportStatusTableHeadCellIds.STATUS,
      label: 'Status',
      tooltip: 'Sync Status on Integration Server',
      tooltipPlacement: 'top',
    },
    {
      id: csvImportStatusTableHeadCellIds.DELETED_AT,
      label: 'Deleted At',
    },
    {
      id: csvImportStatusTableHeadCellIds.DELETED_BY,
      label: 'Deleted By Account ID',
    },
  ],
  cellRenderer: {
    [csvImportStatusTableHeadCellIds.CREATED_AT]: ({ createdAt }: FormattedCsvImportStatus) =>
      falsyCheckDate(createdAt),
    [csvImportStatusTableHeadCellIds.DATA_TYPE]: ({ dataType }: FormattedCsvImportStatus) =>
      dataType,
    [csvImportStatusTableHeadCellIds.SESSION_ID]: ({ sessionId }: FormattedCsvImportStatus) =>
      sessionId,
    [csvImportStatusTableHeadCellIds.MAPPING_STATUS]: ({
      mappingStatus,
    }: FormattedCsvImportStatus) => <MappingStatusDataCell mappingStatus={mappingStatus} />,
    [csvImportStatusTableHeadCellIds.PENDING_WITHOUT_ERROR_IS]: ({
      pendingWithoutErrorCountInIS,
    }: FormattedCsvImportStatus) => {
      return pendingWithoutErrorCountInIS;
    },
    [csvImportStatusTableHeadCellIds.ERROR_ON_IS]: ({
      errorCountInIS,
    }: FormattedCsvImportStatus) => {
      return errorCountInIS;
    },
    [csvImportStatusTableHeadCellIds.TOTAL_ENTRIES]: ({
      totalEntries,
    }: FormattedCsvImportStatus) => {
      return totalEntries;
    },
    [csvImportStatusTableHeadCellIds.IS_AGENT_FETCH_FINISHED]: ({
      isAgentFetchFinished,
    }: FormattedCsvImportStatus) => (
      <StyledAlert
        className="icon-only row-flex-center"
        severity={isAgentFetchFinished ? 'success' : 'error'}
      />
    ),
    [csvImportStatusTableHeadCellIds.STATUS]: ({ status }: FormattedCsvImportStatus) => status,
    [csvImportStatusTableHeadCellIds.DELETED_AT]: ({ deletedAt }: FormattedCsvImportStatus) =>
      falsyCheckDate(deletedAt),
    [csvImportStatusTableHeadCellIds.DELETED_BY]: ({ deleteAccountId }: FormattedCsvImportStatus) =>
      deleteAccountId,
  },
};

const CsvImportStatusTable = ({
  refetchCsvImportStatus,
  csvImportStatusFetchData: { isRequesting, data, filter },
}: TableProps) => {
  const renderOptions = () => {
    return (
      <div>
        <RefreshButtonContainer>
          <IconButton onClick={refetchCsvImportStatus} color="primary">
            <RefreshIcon />
          </IconButton>
        </RefreshButtonContainer>
      </div>
    );
  };

  const { TableComponent } = useSimpleMuiTableBuilder({
    isLoading: isRequesting,
    filter,
    flatList: data,
    cellProps: csvImportStatusTableCellProps,
    tableMaxHeight: '1000px',
    headerProps: {
      tableTitle: 'Import History',
      renderOptions,
    },
    paginationProps: {
      hidePagination: true,
    },
  });

  return <TableComponent />;
};

export default CsvImportStatusTable;
